import type { LessonContent } from "../components/LessonViewer";

const ARCHITECTURE_OVERVIEW = `Sentinel is a multi-domain safety architecture that unifies disparate sensor systems into a single awareness layer. Rather than relying on isolated monitoring tools that react after incidents occur, Sentinel continuously analyzes sensor data across all connected domains to detect emerging threats before they escalate.`;

const DIAGNOSTIC_FRAMEWORK = `The Sentinel diagnostic framework uses pattern recognition across multiple sensor streams to identify anomalies that individual sensors cannot detect alone. By correlating data from multiple domains — such as temperature, pressure, vibration, and chemical sensors — Sentinel can identify cascade risks that would be invisible to single-domain monitoring systems.`;

const INTERVENTION_TEMPLATES = `Intervention templates are pre-configured response actions that Sentinel can deploy automatically when specific threat patterns are detected. Each template defines the intervention type (email, SMS, hardware shutdown, software adjustment, human dispatch, report, alert, or evacuation), the target systems, and the escalation chain.`;

function buildLesson(industry: string, moduleNum: number, lessonTitle: string, contentBody: string, keyPoints: string[], takeaways: string[]): LessonContent {
  return {
    title: `${industry}: ${lessonTitle}`,
    overview: `This lesson explores ${lessonTitle.toLowerCase()} in the context of ${industry} safety operations. You will learn how Sentinel's architecture applies to real-world ${industry} scenarios and how to design effective monitoring and intervention strategies.`,
    sections: [
      {
        heading: "Core Concepts",
        paragraphs: [contentBody],
        keyPoints,
      },
      {
        heading: "Sentinel Architecture in Practice",
        paragraphs: [
          `In ${industry} operations, Sentinel connects sensor data from multiple domains into a unified awareness layer. This means that instead of monitoring each system independently, operators see a correlated view of all safety-relevant data in real time.`,
          `The six-stage Sentinel pipeline — Monitoring, Diagnostics, Risk Classification, Intervention, Verification, and Reporting — ensures that every threat is detected, assessed, responded to, and documented systematically.`,
        ],
        callout: { type: "info", text: `For ${industry}, the key advantage of Sentinel's unified approach is that cross-domain threats — issues that start in one system and cascade to others — are detected before they spread, not after.` },
      },
      {
        heading: "Designing Decision Logic",
        paragraphs: [
          `The heart of Sentinel's autonomous capability is its decision logic. Operators define which sensor states should trigger which interventions. This is done using a truth table approach: for each combination of sensor states across selected domains, you assign the appropriate intervention.`,
          `For example, in ${industry}, you might monitor two domains — each with three states (Normal, Warning, Critical). This gives you 9 possible combinations. For each combination, you decide what Sentinel should do: log a report, send an alert, dispatch a human responder, trigger a hardware shutdown, or order an evacuation.`,
        ],
        callout: { type: "tip", text: `The truth table exercise in this lesson lets you practice this exact process — selecting domains, seeing all combinations, and assigning interventions. This is the same workflow you'll use when configuring Sentinel in production.` },
      },
    ],
    keyTakeaways: takeaways,
  };
}

export function getLessonContent(industry: string, moduleNum: number, lessonTitle: string): LessonContent {
  const baseKeyPoints = [
    "Sentinel unifies multi-domain sensor data into a single awareness layer",
    "The six-stage pipeline ensures systematic threat detection and response",
    "Intervention templates enable sub-second automated response dispatch",
    "Cross-domain correlation detects threats that single-domain systems miss",
  ];

  const baseTakeaways = [
    `Sentinel's unified architecture is specifically designed for ${industry} multi-domain safety`,
    "Truth table decision logic lets you define interventions for every possible sensor combination",
    "Intervention templates provide graduated responses matched to threat severity",
    "Cascade prevention is built into the architecture — not an afterthought",
  ];

  if (moduleNum === 1) {
    return buildLesson(industry, moduleNum, lessonTitle, ARCHITECTURE_OVERVIEW, baseKeyPoints, baseTakeaways);
  }
  if (moduleNum === 2) {
    return buildLesson(industry, moduleNum, lessonTitle, DIAGNOSTIC_FRAMEWORK, [
      "Multi-domain pattern recognition detects anomalies individual sensors miss",
      "Correlation across domains reveals cascade risks before they spread",
      "Continuous analysis replaces periodic sampling for real-time awareness",
      "Predictive diagnostics enable pre-incident intervention, not post-incident reaction",
    ], [
      "Sentinel's diagnostic framework correlates multi-domain data for threat detection",
      "Pattern recognition identifies emerging threats before they become critical",
      "Predictive diagnostics enable proactive intervention in " + industry + " operations",
    ]);
  }
  if (moduleNum === 3) {
    return buildLesson(industry, moduleNum, lessonTitle, INTERVENTION_TEMPLATES, [
      "Intervention templates define pre-configured response actions for specific threat patterns",
      "Templates specify intervention type, target systems, and escalation chain",
      "Graduated responses match the intervention to the threat level",
      "Templates can be customized per industry and per organization",
    ], [
      "Intervention templates enable autonomous, sub-second response to detected threats",
      "Each template defines the intervention type, target, and escalation for " + industry,
      "Graduated responses prevent over-reaction to minor threats and under-reaction to major ones",
    ]);
  }
  return buildLesson(industry, moduleNum, lessonTitle, `This advanced module covers strategic-level Sentinel architecture concepts for ${industry}, including multi-domain correlation, cascade prevention, predictive analytics, and organizational integration.`, baseKeyPoints, baseTakeaways);
}
